/**
 * Import job status values from production.
 */
export type ImportJobStatus =
  | 'SCHEDULED'
  | 'RUNNING'
  | 'COMPLETED'
  | 'FAILED'
  | 'TIMED_OUT'
  | 'CANCELLED'
  | 'SUPERSEDED'
  | 'STORING_RESULTS'
  | 'RESULTS_FETCHED'

/**
 * Get badge color for import job status.
 */
export type StatusColor = 'success' | 'error' | 'info' | 'warning' | 'neutral'

export function getImportStatusColor(status: ImportJobStatus): StatusColor {
  if (status === 'COMPLETED') return 'success'
  if (['FAILED', 'TIMED_OUT', 'CANCELLED'].includes(status)) return 'error'
  if (status === 'SCHEDULED') return 'warning'
  if (['RUNNING', 'STORING_RESULTS', 'RESULTS_FETCHED'].includes(status)) return 'info'
  return 'neutral'
}

/**
 * Format a date string as relative time (e.g., "5m ago", "2d ago", "in 3h").
 */
export function formatRelativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '—'
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return '—'
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()

  // Future dates
  if (diffMs < 0) {
    const futureMins = Math.floor(-diffMs / 60000)
    const futureHours = Math.floor(futureMins / 60)
    const futureDays = Math.floor(futureHours / 24)
    if (futureMins < 1) return 'just now'
    if (futureMins < 60) return `in ${futureMins}m`
    if (futureHours < 24) return `in ${futureHours}h`
    if (futureDays < 7) return `in ${futureDays}d`
    return date.toLocaleDateString()
  }

  // Past dates
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

export function slugify(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple consecutive hyphens with single hyphen
    .replace(/^-|-$/g, '') // Remove leading/trailing hyphens
}

export function toOrdinal(nb: number): string {
  const suffixes: Record<number, string> = {
    1: 'st',
    2: 'nd',
    3: 'rd',
  }
  const lastDigit = nb % 10
  const lastTwoDigits = nb % 100
  // 11, 12, 13 are special cases - they use 'th' suffix
  if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
    return `${nb}th`
  }
  return `${nb}${suffixes[lastDigit] || 'th'}`
}

/**
 * Format platform name for display.
 * - Strips "Ads" suffix: "Meta Ads" -> "Meta"
 * - Converts uppercase codes to title case: "META" -> "Meta"
 */
export function formatPlatformName(name: string): string {
  let formatted = name.replace(/\s+Ads$/i, '')
  // Convert ALL_CAPS to Title Case
  if (formatted === formatted.toUpperCase() && formatted.length > 1) {
    formatted = formatted.charAt(0) + formatted.slice(1).toLowerCase()
  }
  return formatted
}

/**
 * Validate a value against a regex pattern.
 * Returns error message if invalid, null if valid.
 */
export function validatePattern(
  value: string,
  pattern: string | null,
  fieldName: string,
): string | null {
  if (!pattern || !value) return null
  try {
    const regex = new RegExp(pattern)
    if (!regex.test(value)) {
      return `Invalid format for ${fieldName}`
    }
  } catch {
    // Invalid regex pattern, skip validation
  }
  return null
}

/**
 * Build ad library URL from template and parameters.
 */
export function buildAdLibraryUrl(
  template: string,
  advertiserId: string,
  options: {
    bizId?: string
    bizIdPlaceholder?: string
    requiresDateRange?: boolean
    extraParams?: string
    searchType?: 'keyword' | 'advertiser_name'
  } = {},
): string {
  let url = template.replace('{ID}', encodeURIComponent(advertiserId))

  // Handle biz_id placeholder
  if (options.bizIdPlaceholder) {
    if (options.searchType) {
      // TikTok-specific: searchType controls query_type
      // - advertiser_name: query_type=2, include biz_id
      // - keyword: query_type=1, empty biz_id (but param stays in URL)
      if (options.searchType === 'advertiser_name') {
        url = url.replace('query_type=1', 'query_type=2')
        url = url.replace('{BIZ_ID}', options.bizId ? encodeURIComponent(options.bizId) : '')
      } else {
        // keyword search: keep query_type=1, clear biz_id
        url = url.replace('{BIZ_ID}', '')
      }
    } else if (options.bizId) {
      // Non-TikTok: replace placeholder with actual biz_id
      url = url.replace('{BIZ_ID}', encodeURIComponent(options.bizId))
    } else {
      // Non-TikTok: remove biz_id param when not provided
      url = url.replace(/[&?][^&]*=\{BIZ_ID\}/g, '')
    }
    // Clean up malformed URLs (e.g., "?&" or "&&")
    url = url.replace(/\?&/, '?').replace(/&&+/g, '&').replace(/\?$/, '')
  }

  // Helper to get correct separator
  const getSeparator = () => (url.includes('?') ? '&' : '?')

  // Add date range if required (1 year ago to yesterday, in milliseconds)
  if (options.requiresDateRange) {
    const now = new Date()
    const yesterday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() - 1,
      23,
      59,
      59,
      999,
    )
    const oneYearAgo = new Date(yesterday)
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1)
    const startTime = oneYearAgo.getTime()
    const endTime = yesterday.getTime()
    url += `${getSeparator()}start_time=${startTime}&end_time=${endTime}`
  }

  // Add extra params
  if (options.extraParams) {
    url += `${getSeparator()}${options.extraParams}`
  }

  // Validate URL scheme to prevent XSS from malicious templates
  try {
    const parsed = new URL(url)
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
      return ''
    }
  } catch {
    return ''
  }

  return url
}

/**
 * External service URL configuration.
 */
export const GCP_CONSOLE_URL =
  import.meta.env.VITE_GCP_CONSOLE_URL || 'https://console.cloud.google.com'
export const GCP_PROJECT = import.meta.env.VITE_GCP_PROJECT || ''
export const GCP_REGION = import.meta.env.VITE_GCP_REGION || 'europe-west9'
export const APIFY_CONSOLE_URL =
  import.meta.env.VITE_APIFY_CONSOLE_URL || 'https://console.apify.com'

const SAFE_ID_PATTERN = /^[a-zA-Z0-9_.-]+$/

/**
 * Validate that a URL is a safe HTTPS URL.
 */
function isValidHttpsUrl(url: string): boolean {
  try {
    const parsed = new URL(url)
    return parsed.protocol === 'https:'
  } catch {
    return false
  }
}

/**
 * Build Cloud Run job executions URL.
 * Links to the executions list for the job with hash fragment to jump to specific execution.
 * Format: /run/jobs/details/{region}/{job_id}/executions?project={project}#{execution_id}
 */
export function buildCloudRunUrl(jobId: string | null, executionId: string | null): string | null {
  if (!jobId || !SAFE_ID_PATTERN.test(jobId)) return null
  if (!executionId || !SAFE_ID_PATTERN.test(executionId)) return null
  if (!GCP_PROJECT || !GCP_REGION || !SAFE_ID_PATTERN.test(GCP_REGION)) return null
  if (!isValidHttpsUrl(GCP_CONSOLE_URL)) return null
  return `${GCP_CONSOLE_URL}/run/jobs/details/${encodeURIComponent(GCP_REGION)}/${encodeURIComponent(jobId)}/executions?project=${encodeURIComponent(GCP_PROJECT)}#${encodeURIComponent(executionId)}`
}

/**
 * Build Cloud Run logs URL with filters for a specific execution.
 * Format: /logs/query;query={encoded_query};storageScope=project;duration=PT1H?project={project}
 */
export function buildCloudRunLogsUrl(
  jobId: string | null,
  executionId: string | null,
  taskIndex?: number | null,
  startedTs?: string | null,
): string | null {
  if (!jobId || !SAFE_ID_PATTERN.test(jobId)) return null
  if (!executionId || !SAFE_ID_PATTERN.test(executionId)) return null
  if (!GCP_PROJECT || !GCP_REGION || !SAFE_ID_PATTERN.test(GCP_REGION)) return null
  if (!isValidHttpsUrl(GCP_CONSOLE_URL)) return null

  // Build log query filter
  const filters = [
    'resource.type = "cloud_run_job"',
    `resource.labels.job_name = "${jobId}"`,
    `labels."run.googleapis.com/execution_name" = "${executionId}"`,
    `resource.labels.location = "${GCP_REGION}"`,
  ]
  if (taskIndex != null) {
    filters.push(`labels."run.googleapis.com/task_index" = "${taskIndex}"`)
  }
  // Add timestamp filter if provided
  if (startedTs) {
    const ts = new Date(startedTs)
    if (!isNaN(ts.getTime())) {
      filters.push(`timestamp >= "${ts.toISOString()}"`)
    }
  }
  filters.push('severity>=DEFAULT')

  const query = filters.join(' ')
  const encodedQuery = encodeURIComponent(query)

  // Use shorter duration (5 min) when we have a start timestamp, otherwise 1 hour
  const duration = startedTs ? 'PT5M' : 'PT1H'

  return `${GCP_CONSOLE_URL}/logs/query;query=${encodedQuery};storageScope=project;duration=${duration}?project=${encodeURIComponent(GCP_PROJECT)}`
}

/**
 * Build Apify run URL.
 * Returns null if run ID is missing/invalid or URL is invalid.
 */
export function buildApifyUrl(runId: string | null): string | null {
  if (!runId || !SAFE_ID_PATTERN.test(runId)) return null
  if (!isValidHttpsUrl(APIFY_CONSOLE_URL)) return null
  return `${APIFY_CONSOLE_URL}/actors/runs/${encodeURIComponent(runId)}`
}
